// Nap Forecast: predicts the window when the baby is most likely ready to sleep.
//
// Deliberately a pure module with no React and no Firebase, so it can be tested
// against synthetic data and dropped into whichever surface we decide on later.
// Everything is derived from what the room already logs: wakeWindows (completed
// awake periods) and naps (the sleep between them).
//
// The prediction is a rolling median of the baby's own wake windows, bucketed by
// time of day, because a 4-month-old who can manage 90 minutes after the 7am wake
// is usually good for two hours before bedtime. A single daily average smears
// those together and predicts a time that is wrong at both ends of the day.

// Typical wake windows by age in weeks. Mirrors getRecommendedWakeWindow in
// App.js; when the forecast ships, App.js should import this and drop its copy so
// the two can't drift apart.
export const typicalWakeWindow = (ageWeeks) => {
  if (ageWeeks <= 4) return { min: 45, max: 60 };
  if (ageWeeks <= 8) return { min: 60, max: 90 };
  if (ageWeeks <= 16) return { min: 75, max: 120 };
  if (ageWeeks <= 24) return { min: 90, max: 180 };
  return { min: 120, max: 180 };
};

// A sleep session at least this long is treated as night sleep rather than a nap.
// It only affects which sessions feed the prior-nap adjustment below, so a baby
// with an unusually long nap loses one sample rather than getting a wrong answer.
const NIGHT_SLEEP_MIN = 240;

// Slots are keyed off the hour the baby woke up, not the hour of the nap. Evening
// covers the stretch to bedtime and wraps past midnight so night wakings during
// the 18:00-04:00 stretch don't pollute the daytime medians.
export const SLOTS = ['firstWake', 'morning', 'midday', 'afternoon', 'evening'];

export const SLOT_LABELS = {
  firstWake: 'after the first wake',
  morning: 'this morning',
  midday: 'around midday',
  afternoon: 'this afternoon',
  evening: 'before bedtime',
};

export const slotOf = (date) => {
  const h = date.getHours();
  if (h >= 4 && h < 9) return 'firstWake';
  if (h >= 9 && h < 12) return 'morning';
  if (h >= 12 && h < 15) return 'midday';
  if (h >= 15 && h < 18) return 'afternoon';
  return 'evening';
};

// Awake stretches shorter than this are almost always a mis-tap on the Nap tile.
// Anything longer is either a missed log or a night the parent didn't track, and
// including it would drag the median up by an hour.
const MIN_AWAKE_MIN = 15;
const MAX_AWAKE_MIN = 360;

// Below this many samples in a slot the median is noise, so we show the age-based
// range instead and say so. Four is deliberately low: a week of tracking should be
// enough to personalise, or parents will never see the feature turn on.
const MIN_SAMPLES = 4;

const LOOKBACK_DAYS = 14;

// Minimum samples on each side of the short/long nap split before we'll believe a
// difference between them, and the minimum size of that difference (as a share of
// the baby's overall median) before it's worth acting on.
const MIN_EFFECT_SAMPLES = 5;
const MIN_EFFECT_SPREAD = 0.1;

const median = (nums) => {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

// Half the interquartile range, used as the +/- padding on a personalised window.
// A baby with consistent wake windows gets a tight window; an erratic one gets a
// wide one, which is honest rather than falsely precise.
const halfIqr = (nums) => {
  if (nums.length < 4) return null;
  const s = [...nums].sort((a, b) => a - b);
  const q = (p) => s[Math.min(s.length - 1, Math.floor(p * s.length))];
  return (q(0.75) - q(0.25)) / 2;
};

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Pulls awake stretches from both sources. wakeWindows is the direct record, but
// a parent who long-presses to log a past nap creates naps without ever toggling
// wake state, so the gaps between consecutive naps are a second source. Dedupes on
// start time rounded to the minute, since the same stretch can appear in both.
export const collectWakeWindows = ({ naps = [], wakeWindows = [], now = new Date(), days = LOOKBACK_DAYS }) => {
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
  const seen = new Set();
  const out = [];

  // Sleep sessions, used both as a source of awake gaps and to look up what the
  // baby had just slept before each awake stretch.
  const sessions = [...naps]
    .filter((n) => n.start && n.end)
    .map((n) => ({ startMs: new Date(n.start).getTime(), endMs: new Date(n.end).getTime() }))
    .filter((s) => s.endMs > s.startMs)
    .sort((a, b) => a.startMs - b.startMs);

  // Toggling the Nap tile writes the nap's end and the wake period's start from
  // the same timestamp, so these line up exactly. The tolerance is for entries
  // that have since been hand-edited.
  const priorSleepOf = (startMs) => {
    const match = sessions.find((s) => Math.abs(s.endMs - startMs) <= 5 * 60000);
    if (!match) return { priorSleepMinutes: null, priorSleepType: null };
    const mins = (match.endMs - match.startMs) / 60000;
    return { priorSleepMinutes: mins, priorSleepType: mins >= NIGHT_SLEEP_MIN ? 'night' : 'nap' };
  };

  const add = (startMs, endMs) => {
    if (!startMs || !endMs || endMs <= startMs) return;
    if (startMs < cutoff || startMs > now.getTime()) return;
    const minutes = (endMs - startMs) / 60000;
    if (minutes < MIN_AWAKE_MIN || minutes > MAX_AWAKE_MIN) return;
    const key = Math.round(startMs / 60000);
    if (seen.has(key)) return;
    seen.add(key);
    const start = new Date(startMs);
    out.push({ start, minutes, slot: slotOf(start), ...priorSleepOf(startMs) });
  };

  wakeWindows.forEach((w) => add(new Date(w.start).getTime(), new Date(w.end).getTime()));

  for (let i = 0; i < sessions.length - 1; i++) {
    add(sessions[i].endMs, sessions[i + 1].startMs);
  }

  return out.sort((a, b) => a.start - b.start);
};

// How much sooner this baby is ready to sleep again after a short nap.
//
// Deliberately measured rather than assumed. The two-process model says sleep
// pressure dissipates with sleep, so a catnap should leave more of it intact and
// shorten the next wake window, but there is no validated coefficient for infants
// and inventing one would be the same mistake as the wake-window charts. So we
// split this baby's own history at their own median nap length and compare.
//
// Returns null whenever the data can't support the claim, in which case the
// forecast falls back to the unadjusted slot median.
export const priorNapEffect = ({ naps = [], wakeWindows = [], now = new Date() }) => {
  const history = collectWakeWindows({ naps, wakeWindows, now });

  // Each sample is expressed as a multiple of its own slot's median before the
  // groups are compared. Slots have genuinely different lengths, so comparing raw
  // minutes across them would attribute a time-of-day difference to nap length.
  // Normalising also makes the resulting factor directly multiplicative on the
  // slot median the forecast actually uses.
  const slotMedians = {};
  SLOTS.forEach((s) => {
    const vals = history.filter((w) => w.slot === s).map((w) => w.minutes);
    if (vals.length) slotMedians[s] = median(vals);
  });

  // firstWake is excluded: it follows night sleep, which is a different signal.
  const samples = history
    .filter((w) => w.priorSleepType === 'nap' && w.slot !== 'firstWake' && slotMedians[w.slot])
    .map((w) => ({ ...w, ratio: w.minutes / slotMedians[w.slot] }));
  if (samples.length < MIN_EFFECT_SAMPLES * 2) return null;

  const splitPoint = median(samples.map((w) => w.priorSleepMinutes));
  const short = samples.filter((w) => w.priorSleepMinutes < splitPoint);
  const long = samples.filter((w) => w.priorSleepMinutes >= splitPoint);
  if (short.length < MIN_EFFECT_SAMPLES || long.length < MIN_EFFECT_SAMPLES) return null;

  const shortFactor = median(short.map((w) => w.ratio));
  const longFactor = median(long.map((w) => w.ratio));

  // Below this the difference is indistinguishable from noise at these sample
  // sizes, and adjusting by a few minutes would imply precision we don't have.
  if (Math.abs(shortFactor - longFactor) < MIN_EFFECT_SPREAD) return null;

  return {
    splitPoint: Math.round(splitPoint),
    shortFactor: clamp(shortFactor, 0.7, 1.3),
    longFactor: clamp(longFactor, 0.7, 1.3),
    shortCount: short.length,
    longCount: long.length,
  };
};

// Compares the last 7 days of daytime wake windows against the 7 before that.
// A sustained stretch is the earliest signal of a nap transition, which is the
// moment parents most need telling and the one they usually spot weeks late.
export const detectDrift = ({ naps, wakeWindows, now = new Date() }) => {
  const recent = collectWakeWindows({ naps, wakeWindows, now, days: 7 })
    .filter((w) => w.slot !== 'evening');
  const priorAll = collectWakeWindows({ naps, wakeWindows, now, days: 14 })
    .filter((w) => w.slot !== 'evening');
  const cutoff = now.getTime() - 7 * 24 * 60 * 60 * 1000;
  const prior = priorAll.filter((w) => w.start.getTime() < cutoff);

  if (recent.length < 5 || prior.length < 5) return null;

  const recentMed = median(recent.map((w) => w.minutes));
  const priorMed = median(prior.map((w) => w.minutes));
  const change = Math.round(recentMed - priorMed);
  if (Math.abs(change) < 15) return null;

  return {
    changeMinutes: change,
    direction: change > 0 ? 'lengthening' : 'shortening',
    recentMedian: Math.round(recentMed),
    priorMedian: Math.round(priorMed),
    sampleSize: recent.length,
  };
};

/**
 * Forecasts the next sleepy window.
 *
 * @param {Object[]} naps          Sleep sessions, {start, end} ISO strings.
 * @param {Object[]} wakeWindows   Completed awake periods, {start, end}.
 * @param {Object}   wakeState     {awake, startTime} as stored on the room doc.
 * @param {number}   ageWeeks      effectiveAge from App.js.
 * @param {Date}     now
 *
 * @returns {Object} status is 'asleep' when there's nothing to forecast yet,
 *   'unknown' when we don't know when the baby woke, 'due' when the window has
 *   already opened, and 'forecast' otherwise. basis is 'personal' when the numbers
 *   come from this baby and 'typical' when they come from the age table, which the
 *   UI must surface so parents know which one they're looking at.
 */
export const forecastNextNap = ({ naps = [], wakeWindows = [], wakeState = {}, ageWeeks = 0, now = new Date() }) => {
  const drift = detectDrift({ naps, wakeWindows, now });

  if (!wakeState.awake) return { status: 'asleep', drift };
  if (!wakeState.startTime) return { status: 'unknown', drift };

  const wokeAt = new Date(wakeState.startTime);
  if (Number.isNaN(wokeAt.getTime()) || wokeAt > now) return { status: 'unknown', drift };

  const slot = slotOf(wokeAt);
  const history = collectWakeWindows({ naps, wakeWindows, now });
  const inSlotSamples = history.filter((w) => w.slot === slot);
  const inSlot = inSlotSamples.map((w) => w.minutes);

  const typical = typicalWakeWindow(ageWeeks);
  let expectedMin;
  let padMin;
  let basis;

  if (inSlot.length >= MIN_SAMPLES) {
    expectedMin = median(inSlot);
    // Wide enough to be honest about this baby's spread, tight enough to still be
    // actionable. A three-hour "window" is the same as no forecast at all.
    padMin = clamp(halfIqr(inSlot) ?? 15, 10, 30);
    basis = 'personal';
  } else {
    expectedMin = (typical.min + typical.max) / 2;
    padMin = (typical.max - typical.min) / 2;
    basis = 'typical';
  }

  // Adjust for how long the baby actually just slept, but only where this baby's
  // own history shows a difference worth acting on, and only on a personalised
  // forecast. Scaling the generic age table by a personal factor would mix two
  // things that aren't comparable.
  let priorNap = null;
  let adjustment = null;
  if (basis === 'personal' && slot !== 'firstWake') {
    const wokeMs = wokeAt.getTime();
    const justSlept = [...naps]
      .filter((n) => n.start && n.end)
      .map((n) => ({ startMs: new Date(n.start).getTime(), endMs: new Date(n.end).getTime() }))
      .find((s) => Math.abs(s.endMs - wokeMs) <= 5 * 60000);

    if (justSlept) {
      const mins = (justSlept.endMs - justSlept.startMs) / 60000;
      if (mins < NIGHT_SLEEP_MIN) {
        priorNap = Math.round(mins);
        const effect = priorNapEffect({ naps, wakeWindows, now });
        if (effect) {
          const isShort = mins < effect.splitPoint;
          const factor = isShort ? effect.shortFactor : effect.longFactor;
          const before = expectedMin;
          expectedMin *= factor;

          // Recompute the width on what's left once nap length is accounted for.
          // Some of the slot's spread was the catnap-versus-long-nap difference we
          // just corrected for, and leaving it in the pad would keep the window
          // wide enough to swallow the adjustment we just made.
          const residuals = inSlotSamples.map((w) => {
            if (w.priorSleepType !== 'nap') return w.minutes;
            return w.minutes / (w.priorSleepMinutes < effect.splitPoint ? effect.shortFactor : effect.longFactor);
          });
          const residualPad = halfIqr(residuals);
          if (residualPad !== null) padMin = clamp(residualPad, 10, 30);

          adjustment = {
            direction: isShort ? 'short' : 'long',
            factor: Number(factor.toFixed(3)),
            splitPoint: effect.splitPoint,
            deltaMinutes: Math.round(expectedMin - before),
            sampleCounts: { short: effect.shortCount, long: effect.longCount },
          };
        }
      }
    }
  }

  const onset = new Date(wokeAt.getTime() + expectedMin * 60000);
  const windowStart = new Date(onset.getTime() - padMin * 60000);
  const windowEnd = new Date(onset.getTime() + padMin * 60000);

  return {
    status: now > windowEnd ? 'due' : 'forecast',
    windowStart,
    windowEnd,
    onset,
    basis,
    slot,
    sampleSize: inSlot.length,
    expectedMinutes: Math.round(expectedMin),
    awakeMinutes: Math.round((now - wokeAt) / 60000),
    minutesUntilWindow: Math.round((windowStart - now) / 60000),
    priorNapMinutes: priorNap,
    adjustment,
    drift,
  };
};
